/**
 * Rentals (docs/suppliers.md, "Mieten"): the period arithmetic every rental view shares, and what a refused overlap
 * names. No imports: `violations.js` reads it while the app starts, and anything that reaches `number.js` asks Pinia
 * for the settings before there is one.
 */

// The rentals this tab has seen, by id. A server of before refuses an overlap naming only the other rental's id
// (RENTAL_OVERLAPS `{ rentalId }`, today its days too); the message names its days, which every view that writes has
// just loaded.
const seen = new Map()

/** Keeps the periods of rentals as they were answered, for the overlap message. */
export function rememberRentals(rentals) {
  for (const rental of rentals ?? []) {
    if (rental?.id) seen.set(rental.id, { deliveredOn: rental.deliveredOn, pickedUpOn: rental.pickedUpOn ?? null })
  }
}

/** A rental's period as last seen (`{ deliveredOn, pickedUpOn }`), or null for one this tab never loaded. */
export const rentalPeriodOf = id => (id ? (seen.get(id) ?? null) : null)

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

/** "05.10." within `year` (this one), else "05.10.2025": a rental mostly reads within its year. */
export function shortDate(iso, year = new Date().getFullYear()) {
  const match = ISO_DAY.exec(iso ?? "")
  if (!match) return iso ?? ""
  return Number(match[1]) === year ? `${match[3]}.${match[2]}.` : `${match[3]}.${match[2]}.${match[1]}`
}

/**
 * Whether a rental runs on `day` (YYYY-MM-DD): from its delivery to its pick-up, both days included; one not picked up
 * yet has no end.
 */
export const coversDay = (rental, day) =>
  Boolean(rental?.deliveredOn && day) && rental.deliveredOn <= day && (!rental.pickedUpOn || day <= rental.pickedUpOn)

/**
 * The rental a device of a machine is in on `day`. One without a device number is the machine's and counts for every
 * device of it, as the server matches bookings.
 */
export const rentalOfDevice = (rentals, machineId, deviceNumber, day) =>
  (rentals ?? []).find(
    rental =>
      rental.machineId === machineId &&
      (rental.deviceNumber == null || rental.deviceNumber === deviceNumber) &&
      coversDay(rental, day)
  ) ?? null

/**
 * The rental a report booking is in on `day`, as the server matches it: a booking of a device is in that device's
 * rental (`rentalOfDevice`); one naming no device is in the machine's rental when exactly one covers the day, in none
 * where two or more do.
 */
export function bookingRental(rentals, machineId, deviceNumber, day) {
  if (deviceNumber != null) return rentalOfDevice(rentals, machineId, deviceNumber, day)
  const covering = (rentals ?? []).filter(rental => rental.machineId === machineId && coversDay(rental, day))
  return covering.length === 1 ? covering[0] : null
}

/** Newest delivery first, as the server lists them; a running one before a closed one of the same day. */
export const byDeliveryDesc = (a, b) =>
  (b.deliveredOn ?? "").localeCompare(a.deliveredOn ?? "") || Number(Boolean(b.running)) - Number(Boolean(a.running))

/** "Minibagger 3 t #MB-03": a rental's machine and device, as every rental view names it. */
export const deviceLabelOf = rental =>
  `${rental?.machineName ?? ""}${rental?.deviceNumber ? ` #${rental.deviceNumber}` : ""}`.trim()
